/**
 * The add-on strings for the language in use, falling back to English — the
 * same `localizations[lang] || localizations.en` rule the store follows. These
 * strings name controls through `aria-label` and `title`, so a locale that
 * ships without them would otherwise leave a control unnamed, not just
 * untranslated.
 *
 * @param {object|null|undefined} localizations - The store's localizations map
 * @param {string|undefined} lang - The language in use
 * @returns {object} The `addOns` section, never null
 */
export function addOnStrings(localizations, lang) {
    return localizations?.[lang]?.addOns ?? localizations?.en?.addOns ?? {};
}

/**
 * What a locale calls a type, empty for one it does not name. The map is a
 * plain object and the type is a free string, so a type named
 * after something every object carries would otherwise
 * answer with a function.
 *
 * @param {object} strings - The section `addOnStrings` answered with
 * @param {string|null|undefined} type - An add-on's `type`
 * @returns {string} The label, or an empty string
 */
export function addOnTypeLabel(strings, type) {
    const types = strings?.types ?? {};
    return Object.prototype.hasOwnProperty.call(types, type) ? types[type] : '';
}

/**
 * A sentence that names its page, or the plain one for a page with no number
 * to name: a fixed-layout page can come without one.
 *
 * @param {string|undefined} plain - The sentence without a page
 * @param {string|undefined} onPage - The same sentence with a `{page}` placeholder
 * @param {number|null|undefined} page - The page to name
 * @returns {string} The sentence, never null
 */
export function addOnPageSentence(plain, onPage, page) {
    if (page == null) return plain ?? '';

    return (onPage ?? '').replace('{page}', String(page));
}
